
import React, { useState } from 'react';
import { MessageImage } from '@/types/chat';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface MessageImageGalleryProps {
  images: MessageImage[];
  className?: string;
}

const MessageImageGallery: React.FC<MessageImageGalleryProps> = ({
  images,
  className
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!images || images.length === 0) {
    return null;
  }

  const sortedImages = [...images].sort((a, b) => a.display_order - b.display_order);

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % sortedImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + sortedImages.length) % sortedImages.length);
  };

  const getGridLayout = (count: number) => {
    switch (count) {
      case 1:
        return 'grid-cols-1';
      case 2:
        return 'grid-cols-2';
      case 3:
        return 'grid-cols-2';
      default:
        return 'grid-cols-2';
    }
  };

  const getImageAspect = (index: number, total: number) => {
    if (total === 1) return 'aspect-video max-w-sm';
    if (total === 2) return 'aspect-square';
    if (total === 3 && index === 0) return 'aspect-square col-span-2';
    return 'aspect-square';
  };

  return (
    <>
      <div className={cn("grid gap-1 mt-2 max-w-sm", getGridLayout(sortedImages.length), className)}>
        {sortedImages.map((image, index) => (
          <div
            key={image.id}
            className={cn(
              "relative overflow-hidden rounded-md cursor-pointer hover:opacity-90 transition-opacity",
              getImageAspect(index, sortedImages.length)
            )}
            onClick={() => openLightbox(index)}
          >
            <img
              src={image.image_url}
              alt={image.original_filename}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/placeholder.svg';
                target.alt = 'Failed to load image';
              }}
            />
            
            {/* No +N indicator needed since max is 3 images */}
          </div>
        ))}
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 z-[10000]">
          <div className="relative">
            {/* Close button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-10 bg-black/20 hover:bg-black/40 text-white"
              onClick={() => setLightboxOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>

            {/* Navigation buttons */}
            {sortedImages.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-black/20 hover:bg-black/40 text-white"
                  onClick={prevImage}
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-black/20 hover:bg-black/40 text-white"
                  onClick={nextImage}
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}

            {/* Image */}
            <div className="flex items-center justify-center min-h-[400px] max-h-[80vh] bg-black">
              <img
                src={sortedImages[currentImageIndex]?.image_url}
                alt={sortedImages[currentImageIndex]?.original_filename}
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/placeholder.svg';
                }}
              />
            </div>

            {/* Image info */}
            <div className="p-4 bg-background">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">
                    {sortedImages[currentImageIndex]?.original_filename}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {(sortedImages[currentImageIndex]?.file_size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                {sortedImages.length > 1 && (
                  <div className="text-sm text-muted-foreground">
                    {currentImageIndex + 1} of {sortedImages.length}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MessageImageGallery;
